export default function JSVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - JavaScript & React Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for JavaScript &amp;
            React Editor.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I write and run a JavaScript program?
              </h3>
              <p className="mt-1">
                From the home screen tap <strong>New JS</strong>, write your
                code in the editor, then tap the Run button (play icon).
                Console output appears in the panel below the editor. Your
                code runs entirely on your device — no internet connection is
                required.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I preview a React component?
              </h3>
              <p className="mt-1">
                Tap <strong>New React</strong> on the home screen, write a
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  {" "}
                  function App()
                </code>{" "}
                component using JSX, and tap Run. The live preview renders
                below the editor. The component you define is mounted
                automatically — you don't need to call
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  {" "}
                  ReactDOM.createRoot
                </code>{" "}
                unless you want custom control.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does React preview work offline?
              </h3>
              <p className="mt-1">
                Yes. The React runtime and the JSX transpiler are bundled
                inside the app, so the preview works without any internet
                connection. Go ahead and use it on a flight or in a tunnel.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I add CSS to my React preview?
              </h3>
              <p className="mt-1">
                Tap the <strong>Edit CSS</strong> icon in the top-right of
                the preview pane. Styles you write there are applied to your
                preview instantly.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What file types can I open?
              </h3>
              <p className="mt-1">
                Only <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">.js</code>{" "}
                and <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">.jsx</code>{" "}
                files are supported today.
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">.js</code>{" "}
                opens in JavaScript mode,
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">.jsx</code>{" "}
                opens in React mode — the extension dictates the mode.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save my code?
              </h3>
              <p className="mt-1">
                Open the overflow menu (three dots in the top-right of the
                editor) and choose <strong>Save as</strong> to save inside
                the app, or <strong>Save to device</strong> to export the
                file anywhere on your device — Downloads, a cloud sync
                folder, SD card, etc. React code saves as
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  {" "}
                  .jsx
                </code>
                , JavaScript saves as
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  {" "}
                  .js
                </code>
                .
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I use the Examples?
              </h3>
              <p className="mt-1">
                The home screen shows a curated Examples grid with tabs for
                All / JS / React. Tap any card to open that example in the
                editor, then modify and run.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all
                ads with a one-time Pro upgrade from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the
                latest version from Google Play. If a specific program causes
                the crash, check for infinite loops or very large memory
                usage. Email us with your device model, OS version, app
                version, and steps to reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/jsvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/jsvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/jsvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/jsvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> JavaScript &amp; React Editor
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.jsvedit
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
